import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay, Thumbs, Zoom } from 'swiper/modules';
import { isMobile } from './utils';
import 'swiper/css/bundle';
import 'swiper/css/zoom';

Swiper.use([Autoplay, Navigation, Pagination, Thumbs, Zoom]);

const registerZoomControls = (mainSwiper, zoomInButton, zoomOutButton) => {
    if (!zoomInButton || !zoomOutButton || zoomInButton.dataset.zoomBound === 'true') {
        return;
    }

    zoomInButton.addEventListener('click', () => {
        zoomInButton.style.display = 'none';
        zoomOutButton.style.display = 'block';
        mainSwiper.zoom.in();
    });

    zoomOutButton.addEventListener('click', () => {
        zoomInButton.style.display = 'block';
        zoomOutButton.style.display = 'none';
        mainSwiper.zoom.out();
    });

    zoomInButton.dataset.zoomBound = 'true';
    zoomOutButton.dataset.zoomBound = 'true';
};

const initializeProductSliders = (root = document) => {
    const scope = root ?? document;
    scope.querySelectorAll('.product-image-gallery.swiper').forEach((element) => {
        if (element.swiper) {
            element.swiper.update();
            return;
        }

        const galleryContainer = element.closest('.product-image-gallery-container') ?? element.parentElement;
        const zoomButtonZoom = galleryContainer?.querySelector('.product-image-gallery-zoom-button--zoom');
        const zoomButtonClose = galleryContainer?.querySelector('.product-image-gallery-zoom-button--close');
        const thumbsElement = galleryContainer?.querySelector('.product-image-gallery-thumbs.swiper');

        let thumbsSlider = thumbsElement?.swiper ?? null;
        if (thumbsElement && !thumbsSlider) {
            thumbsSlider = new Swiper(thumbsElement, {
                modules: [Thumbs, Zoom],
                spaceBetween: 5,
                slidesPerView: 6,
                watchSlidesVisibility: true,
                watchSlidesProgress: true,
            });
        }

        const mainSwiperOptions = {
            spaceBetween: 10,
            slidesPerView: 1,
            zoom: {
                maxRatio: 5,
                containerClass: 'swiper-zoom-container',
                toggle: false,
            },
            on: {
                slideChangeTransitionStart: () => {
                    if (zoomButtonZoom && zoomButtonClose) {
                        zoomButtonZoom.style.display = 'block';
                        zoomButtonClose.style.display = 'none';
                    }
                },
            },
        };

        if (thumbsSlider) {
            mainSwiperOptions.thumbs = {
                swiper: thumbsSlider,
            };
        }

        const mainSwiper = new Swiper(element, mainSwiperOptions);
        registerZoomControls(mainSwiper, zoomButtonZoom, zoomButtonClose);
    });
};

const initializeCarouselSliders = (root = document) => {
    const scope = root ?? document;
    scope.querySelectorAll('.carousel-slider__slides.swiper').forEach((element) => {
        const wrapper = element.querySelector('.swiper-wrapper');
        const slideCount = wrapper ? wrapper.children.length : 0;

        if (element.swiper) {
            element.swiper.update();
            return;
        }

        new Swiper(element, {
            modules: [Autoplay],
            slidesPerView: 1.3,
            spaceBetween: 24,
            loop: true,
            centeredSlides: slideCount > 1 ? true : false,
            centerInsufficientSlides: slideCount < 1 ? true : false,
            autoplay: {
                delay: 2500,
                disableOnInteraction: false,
            },
            breakpoints: {
                768: {
                    centeredSlides: slideCount > 3 ? true : false,
                    centerInsufficientSlides: slideCount < 3 ? true : false,
                    slidesPerView: 2.3,
                    spaceBetween: 30,
                },
                1024: {
                    centeredSlides: slideCount > 5 ? true : false,
                    centerInsufficientSlides: slideCount < 5 ? true : false,
                    slidesPerView: 4.3,
                    spaceBetween: 30,
                },
                1500: {
                    centeredSlides: slideCount > 6 ? true : false,
                    centerInsufficientSlides: slideCount < 6 ? true : false,
                    slidesPerView: 5.6,
                    spaceBetween: 30,
                },
                1921: {
                    centeredSlides: slideCount > 7 ? true : false,
                    centerInsufficientSlides: slideCount < 7 ? true : false,
                    slidesPerView: 7.4,
                    spaceBetween: 30,
                },
            },
        });
    });
};

const initializeRelatedProductsSliders = (root = document) => {
    const scope = root ?? document;
    scope.querySelectorAll('.related-products-row.swiper').forEach((element) => {
        if (element.swiper) {
            element.swiper.update();
            return;
        }

        if (!isMobile()) {
            return;
        }

        new Swiper(element, {
            modules: [Navigation],
            slidesPerView: 1,
            spaceBetween: 20,
            navigation: {
                nextEl: '.related-products-container .swiper-button-next',
                prevEl: '.related-products-container .swiper-button-prev',
            },
        });
    });
};

const initializeTestimonialsSliders = (root = document) => {
    const scope = root ?? document;
    scope.querySelectorAll('.testimonials-block__content.swiper').forEach((element) => {
        if (element.swiper) {
            element.swiper.update();
            return;
        }

        new Swiper(element, {
            modules: [Navigation],
            slidesPerView: 1,
            spaceBetween: 15,
            loop: true,
            navigation: {
                nextEl: '.testimonials-block .swiper-button-next',
                prevEl: '.testimonials-block .swiper-button-prev',
            },
            breakpoints: {
                768: {
                    slidesPerView: 2,
                },
                1024: {
                    slidesPerView: 3,
                },
            },
        });
    });
};

const initializeLogosSliders = (root = document) => {
    const scope = root ?? document;
    scope.querySelectorAll('.logos-block__slider.swiper').forEach((element) => {
        if (element.swiper) {
            element.swiper.update();
            return;
        }

        new Swiper(element, {
            modules: [Navigation],
            slidesPerView: 2,
            spaceBetween: 20,
            navigation: {
                nextEl: '.logos-block .swiper-button-next',
                prevEl: '.logos-block .swiper-button-prev',
            },
            breakpoints: {
                768: {
                    slidesPerView: 4,
                },
                1024: {
                    slidesPerView: 7,
                },
            },
        });
    });
};

const initializeAllSliders = (root = document) => {
    initializeProductSliders(root);
    initializeCarouselSliders(root);
    initializeRelatedProductsSliders(root);
    initializeTestimonialsSliders(root);
    initializeLogosSliders(root);
};

let htmxHandlersRegistered = false;

const handleDynamicContent = () => {
    initializeAllSliders(document);
};

const registerHtmxHandlers = () => {
    if (htmxHandlersRegistered || !document.body) {
        return;
    }

    document.body.addEventListener('htmx:afterSwap', handleDynamicContent);
    document.body.addEventListener('htmx:afterSettle', handleDynamicContent);
    document.body.addEventListener('htmx:oobAfterSwap', handleDynamicContent);

    htmxHandlersRegistered = true;
};

const onReady = () => {
    initializeAllSliders(document);
    registerHtmxHandlers();
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
} else {
    onReady();
}
